import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import NoteRenderer from "@/components/NoteRenderer";
import StudyPanel from "@/components/StudyPanel";
import ChatWithNote from "@/components/ChatWithNote";
import HandwritingPicker from "@/components/HandwritingPicker";
import { Copy, Download, FileText, ArrowLeft, Trash2, Share2, Users, Check, X, ExternalLink, FileType, Minimize2, Maximize2, Layers, PenLine, BookOpen, GraduationCap, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

function toMarkdown(note) {
  const n = note.notes || {};
  const lines = [];
  if (n.format === "meeting") {
    lines.push(`# ${n.title || note.title}`);
    if (n.objective) lines.push(`\n**Objective:** ${n.objective}`);
    if (n.one_line_summary) lines.push(`\n> ${n.one_line_summary}`);
    if (n.key_decisions?.length) { lines.push(`\n## Key Decisions`); n.key_decisions.forEach((d) => lines.push(`- ${d}`)); }
    if (n.important_discussion_points?.length) { lines.push(`\n## Discussion Points`); n.important_discussion_points.forEach((d) => lines.push(`- ${d}`)); }
    if (n.goals?.length) { lines.push(`\n## Goals`); n.goals.forEach((g) => lines.push(`- ${g}`)); }
    if (n.action_items?.length) {
      lines.push(`\n## Action Items`);
      lines.push(`| Task | Owner | Deadline |`);
      lines.push(`| --- | --- | --- |`);
      n.action_items.forEach((a) => lines.push(`| ${a.task} | ${a.owner || "—"} | ${a.deadline || "—"} |`));
    }
    if (n.risks?.length) { lines.push(`\n## Risks`); n.risks.forEach((r) => lines.push(`- ${r}`)); }
    if (n.pending_questions?.length) { lines.push(`\n## Pending Questions`); n.pending_questions.forEach((q) => lines.push(`- ${q}`)); }
    if (n.follow_ups?.length) { lines.push(`\n## Follow-Ups`); n.follow_ups.forEach((f) => lines.push(`- ${f}`)); }
    if (n.important_numbers?.length) {
      lines.push(`\n## Important Numbers`);
      n.important_numbers.forEach((x) => lines.push(`- **${x.label}:** ${x.value}`));
    }
    return lines.join("\n");
  }

  lines.push(`# ${n.topic || note.title}`);
  if (n.one_line_summary) lines.push(`\n> ${n.one_line_summary}`);
  if (n.core_concepts?.length) {
    lines.push(`\n## Core Concepts`);
    n.core_concepts.forEach((c) => lines.push(`- ${"⭐".repeat(c.importance || 2)} ${c.text}`));
  }
  if (n.keywords?.length) lines.push(`\n## Keywords\n\n${n.keywords.map((k) => `\`${k}\``).join(" · ")}`);
  if (n.definitions?.length) {
    lines.push(`\n## Definitions`);
    n.definitions.forEach((d) => lines.push(`- **${d.term}** → ${d.definition}`));
  }
  if (n.flowcharts?.length) {
    lines.push(`\n## Flowcharts`);
    n.flowcharts.forEach((f) => {
      if (f.title) lines.push(`\n**${f.title}**`);
      lines.push(f.steps.map((s) => s).join("\n↓\n"));
    });
  }
  if (n.tables?.length) {
    lines.push(`\n## Tables`);
    n.tables.forEach((t) => {
      if (t.title) lines.push(`\n**${t.title}**`);
      lines.push(`| ${(t.headers || []).join(" | ")} |`);
      lines.push(`| ${(t.headers || []).map(() => "---").join(" | ")} |`);
      (t.rows || []).forEach((row) => lines.push(`| ${row.join(" | ")} |`));
    });
  }
  if (n.formulas?.length) {
    lines.push(`\n## Formula Sheet`);
    n.formulas.forEach((f) => {
      lines.push(`\n- \`${f.formula}\``);
      lines.push(`  - **Meaning:** ${f.meaning}`);
      if (f.units) lines.push(`  - **Units:** ${f.units}`);
      if (f.when_to_use) lines.push(`  - **When:** ${f.when_to_use}`);
    });
  }
  if (n.important_facts?.length) { lines.push(`\n## Important Facts`); n.important_facts.forEach((f) => lines.push(`- ${f}`)); }
  if (n.frequently_emphasized?.length) { lines.push(`\n## 🔥 Frequently Emphasized`); n.frequently_emphasized.forEach((f) => lines.push(`- ${f}`)); }
  if (n.one_minute_revision?.length) { lines.push(`\n## One-Minute Revision`); n.one_minute_revision.forEach((r, i) => lines.push(`${i + 1}. ${r}`)); }
  if (n.exam_questions?.length) { lines.push(`\n## Possible Exam Questions`); n.exam_questions.forEach((q, i) => lines.push(`${i + 1}. (${q.type || "short"}) ${q.question}`)); }
  return lines.join("\n");
}

export default function NoteViewer() {
  const { id } = useParams();
  const [note, setNote] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState({ is_public: false, share_slug: null });
  const [teams, setTeams] = useState([]);
  const [density, setDensity] = useState(() => localStorage.getItem("note_density") || "compact");
  const [renderMode, setRenderMode] = useState(() => localStorage.getItem("note_mode") || "print");
  const [hwFont, setHwFont] = useState(() => localStorage.getItem("note_hw_font") || "Caveat");
  const [activeTab, setActiveTab] = useState("notes"); // "notes" | "study"
  const [chatOpen, setChatOpen] = useState(false);
  const printableRef = useRef(null);
  const nav = useNavigate();

  const toggleDensity = () => {
    const next = density === "compact" ? "standard" : "compact";
    setDensity(next);
    localStorage.setItem("note_density", next);
    toast.success(`${next[0].toUpperCase()}${next.slice(1)} density`);
  };

  const toggleMode = () => {
    const next = renderMode === "print" ? "handwritten" : "print";
    setRenderMode(next);
    localStorage.setItem("note_mode", next);
    toast.success(next === "handwritten" ? "Handwritten mode on" : "Back to print");
  };

  const pickHwFont = (font) => {
    setHwFont(font);
    localStorage.setItem("note_hw_font", font);
    toast.success(`Handwriting: ${font}`);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/notes/${id}`);
        setNote(data);
        setShareInfo({ is_public: !!data.is_public, share_slug: data.share_slug });
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [id]);

  const copy = async () => {
    if (!note) return;
    try {
      await navigator.clipboard.writeText(toMarkdown(note));
      toast.success("Copied as Markdown");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadMd = () => {
    if (!note) return;
    const blob = new Blob([toMarkdown(note)], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(note.title || "notes").replace(/\s+/g, "-")}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadPdf = async () => {
    if (!printableRef.current) return;
    setExporting(true);
    try {
      // Wait for every custom web font (Caveat / Kalam / Patrick Hand / etc.)
      // to finish loading. Without this, html2canvas rasterises mid-swap and
      // keyword <mark> highlight boxes end up offset from their text.
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch { /* ignore */ }
      }
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf()
        .set({
          margin: [12, 12, 12, 12],
          filename: `${(note.title || "notes").replace(/\s+/g, "-")}.pdf`,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            onclone: (clonedDoc) => {
              clonedDoc.body.classList.add("pdf-mode");
            },
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: {
            mode: ["avoid-all", "css", "legacy"],
            avoid: [
              "section",
              "table",
              "tr",
              "img",
              "figure",
              "li",
              ".pdf-avoid",
            ],
          },
        })
        .from(printableRef.current)
        .save();
    } catch {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const downloadDocx = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API}/notes/${id}/export/docx`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("DOCX export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(note.title || "notes").replace(/\s+/g, "-")}.docx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("DOCX downloaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadAnki = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API}/notes/${id}/export/anki`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Anki export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(note.title || "notes").replace(/\s+/g, "-")}.apkg`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Anki deck downloaded", {
        description: "Double-click the .apkg file to import into Anki.",
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const toggleShare = async () => {
    try {
      const { data } = await api.post(`/notes/${id}/share`);
      setShareInfo({ is_public: data.is_public, share_slug: data.share_slug });
      if (data.is_public) toast.success("Public link enabled");
      else toast.success("Link disabled");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openShare = async () => {
    setShareOpen(true);
  };

  const publicUrl = shareInfo.share_slug
    ? `${window.location.origin}/share/${shareInfo.share_slug}`
    : "";

  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const openTeamDialog = async () => {
    setTeamOpen(true);
    try {
      const { data } = await api.get("/teams");
      setTeams(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shareToTeam = async (teamId) => {
    try {
      await api.post(`/notes/${id}/team`, { team_id: teamId });
      setNote((prev) => prev && { ...prev, team_ids: Array.from(new Set([...(prev.team_ids || []), teamId])) });
      toast.success("Shared to team");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const unshareFromTeam = async (teamId) => {
    try {
      await api.delete(`/notes/${id}/team/${teamId}`);
      setNote((prev) => prev && { ...prev, team_ids: (prev.team_ids || []).filter((t) => t !== teamId) });
      toast.success("Removed from team");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/notes/${id}`);
      toast.success("Deleted");
      nav("/app");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#F0F9FF]">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-16">
          <p data-testid="note-error" className="font-mono text-sm text-[#FF3333]">{error}</p>
          <Link to="/app" className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest underline">
            <ArrowLeft className="w-3 h-3" /> Back to library
          </Link>
        </main>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="min-h-screen bg-[#F0F9FF]">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-16">
          <div data-testid="note-loading" className="font-mono text-sm text-zinc-500">Loading note…</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F9FF]">
      <Navbar />

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="no-print flex items-center gap-2 mb-6">
          <Link data-testid="back-to-library" to="/app" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest hover:text-[#3B82F6] transition-colors mr-2">
            <ArrowLeft className="w-3 h-3" /> Library
          </Link>
          <button
            data-testid="tab-notes"
            onClick={() => setActiveTab("notes")}
            className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black transition-colors ${
              activeTab === "notes" ? "bg-black text-white" : "hover:bg-black hover:text-white"
            }`}
          >
            <BookOpen className="w-3.5 h-3.5" /> Notes
          </button>
          <button
            data-testid="tab-study"
            onClick={() => setActiveTab("study")}
            className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black transition-colors ${
              activeTab === "study" ? "bg-black text-white" : "hover:bg-black hover:text-white"
            }`}
          >
            <GraduationCap className="w-3.5 h-3.5" /> Study
          </button>
          <button
            data-testid="chat-open-btn"
            onClick={() => setChatOpen(true)}
            className="ml-auto inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black bg-[#3B82F6] text-white btn-brutal"
            title="Chat about this note"
          >
            <MessageSquareText className="w-3.5 h-3.5" /> Ask AI
          </button>
        </div>

        {activeTab === "notes" && (
        <>
        <div className="no-print flex flex-wrap items-center justify-end gap-3 mb-8">
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="btn-density"
              onClick={toggleDensity}
              title={`Switch to ${density === "compact" ? "standard" : "compact"} density`}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
            >
              {density === "compact" ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
              {density === "compact" ? "Standard" : "Compact"}
            </button>
            <button
              data-testid="btn-mode"
              onClick={toggleMode}
              title={renderMode === "print" ? "Switch to handwritten mode" : "Switch back to print"}
              className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 transition-colors ${
                renderMode === "handwritten"
                  ? "border-black bg-[#FEF3C7] hover:bg-black hover:text-white"
                  : "border-black hover:bg-black hover:text-white"
              }`}
            >
              <PenLine className="w-3.5 h-3.5" />
              {renderMode === "print" ? "Handwritten" : "Print"}
            </button>
            {renderMode === "handwritten" && (
              <HandwritingPicker value={hwFont} onChange={pickHwFont} />
            )}
            <button
              data-testid="btn-share"
              onClick={openShare}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
            <button
              data-testid="btn-team-share"
              onClick={openTeamDialog}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
            >
              <Users className="w-3.5 h-3.5" /> Teams
            </button>
            <button
              data-testid="btn-copy"
              onClick={copy}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <button
              data-testid="btn-md"
              onClick={downloadMd}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> MD
            </button>
            <button
              data-testid="btn-docx"
              onClick={downloadDocx}
              disabled={exporting}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-50"
            >
              <FileType className="w-3.5 h-3.5" /> DOCX
            </button>
            <button
              data-testid="btn-anki"
              onClick={downloadAnki}
              disabled={exporting}
              title="Download as Anki flashcard deck (.apkg)"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-50"
            >
              <Layers className="w-3.5 h-3.5" /> Anki
            </button>
            <button
              data-testid="btn-pdf"
              onClick={downloadPdf}
              disabled={exporting}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 bg-[#3B82F6] text-white btn-brutal disabled:opacity-60"
            >
              <FileText className="w-3.5 h-3.5" /> {exporting ? "…" : "PDF"}
            </button>
            <button
              data-testid="btn-delete"
              onClick={remove}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 border-2 border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div ref={printableRef} className="bg-white border border-[#BFDBFE] p-8 sm:p-12">
          {note.siblings?.length > 0 && (
            <div
              data-testid="siblings-banner"
              className="mb-8 border-2 border-black bg-[#DBEAFE] p-4"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#3B82F6]">
                / Related topic notes
              </p>
              <p className="mt-1 font-heading font-bold">
                This note was split from a multi-topic source. Jump to a sibling:
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {note.siblings.map((s) => (
                  <Link
                    key={s.id}
                    data-testid={`sibling-link-${s.id}`}
                    to={`/app/note/${s.id}`}
                    className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 bg-white border-2 border-black hover:bg-black hover:text-white transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {s.title}
                  </Link>
                ))}
              </div>
            </div>
          )}
          <NoteRenderer notes={note.notes} density={density} mode={renderMode} hwFont={hwFont} />
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="note-signals">
            {typeof note.generation_ms === "number" && note.generation_ms > 0 && (
              <span
                data-testid="signal-generation-time"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-black text-[#BFDBFE]"
                title="Total generation time"
              >
                <span aria-hidden>⚡</span>
                Generated in {(note.generation_ms / 1000).toFixed(note.generation_ms < 10000 ? 1 : 0)}s
              </span>
            )}
            <span
              data-testid="signal-one-page"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-[#BFDBFE] text-black border border-black"
              title="Designed to fit on a single revision page"
            >
              <span aria-hidden>📄</span>
              Fits on 1 page
            </span>
          </div>
        </div>
        </>
        )}

        {activeTab === "study" && (
          <div data-testid="study-tab-content">
            <StudyPanel noteId={id} />
          </div>
        )}
      </main>

      <ChatWithNote noteId={id} open={chatOpen} onClose={() => setChatOpen(false)} />

      {shareOpen && (
        <Modal onClose={() => setShareOpen(false)} title="Share this note" testId="share-modal">
          <p className="text-sm text-zinc-600">
            When enabled, anyone with the link can view this note (read-only). No login required.
          </p>
          <button
            data-testid="toggle-public"
            onClick={toggleShare}
            className={`mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-3 border-2 border-black transition-colors ${
              shareInfo.is_public ? "bg-[#3B82F6] text-white" : "hover:bg-black hover:text-white"
            }`}
          >
            {shareInfo.is_public ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
            {shareInfo.is_public ? "Public link is on" : "Enable public link"}
          </button>

          {shareInfo.is_public && publicUrl && (
            <div className="mt-4">
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Public URL</p>
              <div className="mt-2 flex items-center gap-2">
                <input
                  data-testid="public-url"
                  readOnly
                  value={publicUrl}
                  className="flex-1 px-3 py-2 bg-[#F0F9FF] border-2 border-black font-mono text-xs"
                />
                <button
                  data-testid="copy-public-url"
                  onClick={copyPublicUrl}
                  className="p-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="p-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          )}
        </Modal>
      )}

      {teamOpen && (
        <Modal onClose={() => setTeamOpen(false)} title="Share to a team" testId="team-modal">
          <p className="text-sm text-zinc-600">
            Team members will see this note in their team's shared library.
          </p>
          {teams.length === 0 ? (
            <div className="mt-6">
              <p className="text-sm text-zinc-500">You don't have any teams yet.</p>
              <Link
                to="/app/teams"
                className="mt-3 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 bg-[#3B82F6] text-white btn-brutal"
              >
                <Users className="w-4 h-4" /> Create or join a team
              </Link>
            </div>
          ) : (
            <ul className="mt-4 space-y-2 max-h-72 overflow-y-auto">
              {teams.map((t) => {
                const isShared = (note.team_ids || []).includes(t.id);
                return (
                  <li key={t.id} data-testid={`team-row-${t.id}`} className="flex items-center justify-between gap-3 border-2 border-black px-3 py-2">
                    <div>
                      <div className="font-heading font-bold">{t.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{t.member_count} member{t.member_count === 1 ? "" : "s"}</div>
                    </div>
                    <button
                      data-testid={`toggle-team-share-${t.id}`}
                      onClick={() => (isShared ? unshareFromTeam(t.id) : shareToTeam(t.id))}
                      className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest px-3 py-2 transition-colors ${
                        isShared ? "bg-[#3B82F6] text-white" : "border-2 border-black hover:bg-black hover:text-white"
                      }`}
                    >
                      {isShared ? <><Check className="w-3.5 h-3.5" /> Shared</> : <>Share</>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title, testId }) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white border-2 border-black p-6 shadow-[8px_8px_0_0_#0A0A0A]"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="font-heading font-bold text-2xl tracking-tight">{title}</h2>
          <button
            data-testid={`${testId}-close`}
            onClick={onClose}
            className="p-1 hover:bg-black hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
